import React, { useState, useMemo } from 'react';
import { Eye, Settings } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

import { EventType } from 'types/event.types';
import { queryClient } from '@api/queryClient';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { TemplateSelectionModal } from '@event-editor/components/TemplateSelectionModal/TemplateSelectionModal';
import { SurveyTemplateTypes } from 'types/survey_template.types';
import { Question } from 'types/question.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { EventEditorEmptyQuestions } from '@event-editor/components/EventEditorEmptyQuestions';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { DraggableQuestionsList } from '@event-editor/components/DraggableQuestionsList';
import { reorderEventQuestionnaireQuestions } from '@event-editor/api/questionnaire/reorderEventQuestionnaireQuestions';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';

import {
  QuestionnairePreviewModal,
  EditQuestionnaireQuestionSideSheet,
  PastEventEmptyQuestionnaire,
  QuestionnaireCutoffModal
} from '../components';
import { EmptyMultiSessionEventQuestionnaire } from '../components/EmptyMultiSessionEventQuestionnaire';

export const QuestionnaireQuestionsSection = (): React.ReactElement => {
  const event = useEditableEvent();
  const sessionsQuery = useSessionsQuery(event);
  const { showToast } = useToaster();

  const isCancelled = !!event.scheduledDeletionAt;
  const questions = useMemo(
    () =>
      event.preEventQuestions
        ?.filter((q) => !q.isDeleted)
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)) || [],
    [event.preEventQuestions]
  );

  const [selectedQuestion, setSelectedQuestion] = useState<Question | undefined>(undefined);
  const [isTemplateSelectionModalOpen, setIsTemplateSelectionModalOpen] = useState(false);
  const [isEditQuestionSideSheetOpen, setIsEditQuestionSideSheetOpen] = useState(false);
  const [isQuestionPreviewModalOpen, setIsQuestionPreviewModalOpen] = useState(false);
  const [isQuestionnaireCutoffModalOpen, setIsQuestionnaireCutoffModalOpen] = useState(false);

  const isEmptyMultiSession = useMemo(() => {
    return event.eventType === EventType.MULTI_SESSIONS && sessionsQuery.data?.length === 0;
  }, [sessionsQuery.data, event.eventType]);

  const reorderQuestionsMutation = useMutation({
    mutationFn: (variables: { eventId: number; questions: Question[] }) =>
      reorderEventQuestionnaireQuestions({
        eventId: variables.eventId,
        questionSortOrders: variables.questions.map((question, index) => ({
          questionId: question.id!,
          sortOrder: index
        }))
      }),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey: eventEditorQueryKeys.event(variables.eventId)
      });

      // Snapshot the previous value
      const previousEvent = queryClient.getQueryData(eventEditorQueryKeys.event(variables.eventId));

      // Optimistically update the cache with reordered questions
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) => {
        if (!oldData?.event.preEventQuestions) return oldData;

        return {
          event: {
            ...oldData.event,
            preEventQuestions: oldData.event.preEventQuestions.map(
              (existingQuestion: Question) =>
                variables.questions.find((q) => q.id === existingQuestion.id) || existingQuestion
            )
          }
        };
      });

      // Return a context object with the snapshotted value
      return { previousEvent };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousEvent) {
        queryClient.setQueryData(
          eventEditorQueryKeys.event(variables.eventId),
          context.previousEvent
        );
      }
      showToast('Failed to reorder questions', { variant: ToasterVariant.ERROR });
    },
    onSuccess: (result, variables) => {
      const { event: eventResult } = result.reorderEventQuestionnaireQuestions;

      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                preEventQuestions: eventResult.preEventQuestions
              }
            }
          : oldData
      );
    }
  });

  return (
    <div>
      <div className="tw-mb-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          <h2
            className={cn(
              'tw-m-0 tw-mb-1 tw-text-xl tw-font-medium tw-leading-7',
              isCancelled && 'tw-opacity-40'
            )}
          >
            Event questionnaire
          </h2>

          {/* Action buttons in header - only show when questions exist */}
          {questions.length > 0 && !isEmptyMultiSession && (
            <div className="tw-flex tw-items-center tw-gap-2">
              {/* Preview button */}
              <Button
                size="xxs"
                variant="secondary"
                disabled={isCancelled || event.hasEnded}
                className="tw-gap-1 tw-rounded-xl tw-leading-4"
                onClick={() => setIsQuestionPreviewModalOpen(true)}
              >
                <Eye className="tw-text-black" size={16} />
                Preview
              </Button>
              {/* Questionnaire cutoff setting */}
              <Button
                size="xxs"
                variant="secondary"
                disabled={isCancelled || event.hasEnded}
                className="tw-gap-1 tw-rounded-xl tw-leading-4"
                onClick={() => setIsQuestionnaireCutoffModalOpen(true)}
              >
                <Settings className="tw-text-black" size={16} />
                Questionnaire cutoff
              </Button>
            </div>
          )}
        </div>
      </div>

      {isEmptyMultiSession ? (
        <EmptyMultiSessionEventQuestionnaire />
      ) : (
        <>
          {questions.length === 0 && event.hasEnded && <PastEventEmptyQuestionnaire />}

          {questions.length === 0 && !event.hasEnded && (
            <EventEditorEmptyQuestions
              disabled={isCancelled || event.hasEnded}
              onStartFromScratch={() => setIsEditQuestionSideSheetOpen(true)}
              onUseTemplate={() => setIsTemplateSelectionModalOpen(true)}
            />
          )}

          {questions.length > 0 && (
            <DraggableQuestionsList
              disabled={event.hasEnded || isCancelled || reorderQuestionsMutation.isPending}
              questions={questions}
              onReorder={(reorderedQuestions) => {
                if (!event.id) return;
                reorderQuestionsMutation.mutate({
                  eventId: event.id,
                  questions: reorderedQuestions
                });
              }}
              onAddQuestion={() => setIsEditQuestionSideSheetOpen(true)}
              onEditQuestion={(question) => {
                setSelectedQuestion(question);
                setIsEditQuestionSideSheetOpen(true);
              }}
            />
          )}
        </>
      )}

      <TemplateSelectionModal
        open={isTemplateSelectionModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsTemplateSelectionModalOpen(false);
          }
        }}
        templateType={SurveyTemplateTypes.EVENT_QUESTIONNAIRE}
      />
      <QuestionnairePreviewModal
        open={isQuestionPreviewModalOpen}
        onOpenChange={setIsQuestionPreviewModalOpen}
        questions={questions}
      />
      <EditQuestionnaireQuestionSideSheet
        open={isEditQuestionSideSheetOpen}
        onOpenChange={(open) => {
          setIsEditQuestionSideSheetOpen(open);
          if (!open) {
            setTimeout(() => {
              setSelectedQuestion(undefined);
            }, 300);
          }
        }}
        question={selectedQuestion}
      />
      <QuestionnaireCutoffModal
        open={isQuestionnaireCutoffModalOpen}
        onOpenChange={setIsQuestionnaireCutoffModalOpen}
      />
    </div>
  );
};
